import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { visitsAPI, travelAPI, clockAPI, mileageAPI } from '../lib/api';
import { formatDate, formatTime, getStatusClass, getCurrentLocation, capitalize } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Car, 
  MapPin, 
  Clock, 
  Play, 
  Square, 
  FileText,
  Navigation,
  CheckCircle,
  Loader2,
  Calendar,
  User,
  ChevronRight,
  History,
  Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import LiveLocationHeartbeat from "../components/LiveLocationHeartbeat";

export default function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [nextVisit, setNextVisit] = useState(null);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [pastVisits, setPastVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Mileage tracking state
  const [activeMileage, setActiveMileage] = useState(null);
  const [odometerDialog, setOdometerDialog] = useState({ open: false, type: null });
  const [startOdometer, setStartOdometer] = useState('');
  const [endOdometer, setEndOdometer] = useState('');
  const [shiftNotes, setShiftNotes] = useState('');
  const [trackMileage, setTrackMileage] = useState(true);
  const [mileageSummary, setMileageSummary] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [nextRes, upcomingRes, pastRes, mileageRes, summaryRes] = await Promise.all([
        visitsAPI.getMyNext(),
        visitsAPI.getMyUpcoming(),
        visitsAPI.getMyPast(),
        mileageAPI.getActive().catch(() => ({ data: null })),
        mileageAPI.getSummary({}).catch(() => ({ data: null }))
      ]);
      setNextVisit(nextRes.data);
      setUpcomingVisits(upcomingRes.data || []);
      setPastVisits(pastRes.data || []);
      setActiveMileage(mileageRes.data);
      setMileageSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to fetch visits:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getGPS = async () => {
    setGpsStatus('acquiring');
    try {
      const location = await getCurrentLocation();
      setGpsStatus('acquired');
      return location;
    } catch (err) {
      setGpsStatus('error');
      toast.error('GPS unavailable - continuing without location');
      return null;
    }
  };

  const handleStartTravel = async () => {
    if (!nextVisit) return;
    setActionLoading('travel');
    try {
      const gps = await getGPS();
      await travelAPI.start(nextVisit.id, gps);
      toast.success('Travel started');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start travel');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArrived = async () => {
    if (!nextVisit?.active_travel) return;
    setActionLoading('arrived');
    try {
      const gps = await getGPS();
      await travelAPI.arrived(nextVisit.active_travel.id, gps, null);
      toast.success('Arrival recorded');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record arrival');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClockOn = async () => {
    if (!nextVisit) return;
    setTrackMileage(true);
    setStartOdometer('');
    setOdometerDialog({ open: true, type: 'start' });
  };

  const handleClockOnWithOdometer = async () => {
    try {
      setActionLoading('clockOn');
      const gps = await getCurrentLocation();

      if (trackMileage) {
        const odometerValue = parseFloat(startOdometer);

        if (isNaN(odometerValue) || odometerValue < 0) {
          toast.error('Please enter a valid start odometer reading');
          return;
        }

        await mileageAPI.create({
          visit_id: nextVisit.id,
          start_odometer: odometerValue,
          notes: `Shift start - ${nextVisit.client_name || 'Visit'}`
        });
      }

      await clockAPI.clockOn(nextVisit.id, gps);

      setOdometerDialog({ open: false, type: null });
      setStartOdometer('');
      setTrackMileage(true);

      toast.success(trackMileage ? 'Clocked on with odometer recorded' : 'Clocked on without KM tracking');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock on');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClockOff = async () => {
    if (!nextVisit?.active_clock) return;
    setEndOdometer('');
    setOdometerDialog({ open: true, type: 'end' });
  };

  const handleClockOffWithOdometer = async () => {
    if (!nextVisit?.active_clock?.id) {
      toast.error('No active clock record found. Please refresh and try again.');
      return;
    }

    if (!shiftNotes || !shiftNotes.trim()) {
      toast.error('Please enter shift notes before clocking off');
      return;
    }

    const hasMileage = !!activeMileage;
    let odometerValue = null;

    if (hasMileage) {
      odometerValue = parseFloat(endOdometer);

      if (isNaN(odometerValue) || odometerValue < 0) {
        toast.error('Please enter a valid end odometer reading');
        return;
      }

      if (odometerValue < activeMileage.start_odometer) {
        toast.error(`End odometer (${odometerValue}) cannot be less than start (${activeMileage.start_odometer})`);
        return;
      }
    }

    try {
      setActionLoading('clockOff');

      let gps = null;
      try {
        gps = await getCurrentLocation();
      } catch (gpsErr) {
        console.warn('GPS unavailable during clock off:', gpsErr);
      }

      if (hasMileage) {
        await mileageAPI.update(activeMileage.id, {
          end_odometer: odometerValue,
          notes: shiftNotes.trim()
        });
      }

      await clockAPI.clockOff(
        nextVisit.active_clock.id,
        gps,
        shiftNotes.trim(),
        odometerValue
      );

      toast.success(hasMileage ? 'Clocked off successfully' : 'Clocked off successfully. No KM recorded.');

      setOdometerDialog({ open: false, type: null });
      setEndOdometer('');
      setShiftNotes('');
      setActiveMileage(null);
      setNextVisit(null);
      setUpcomingVisits([]);
      setActionLoading(null);

    } catch (err) {
      console.error('Clock off failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to clock off');
      setActionLoading(null);
    }
  };


  const workflowState = String(
    nextVisit?.workflow_state ||
    nextVisit?.status ||
    'scheduled'
  ).toLowerCase();

  const normalizedWorkflowState =
    workflowState === 'upcoming' || workflowState === 'assigned'
      ? 'scheduled'
      : workflowState === 'clocked_on'
        ? 'in_progress'
        : workflowState === 'complete'
          ? 'completed'
          : workflowState;

  const isTraveling =
    nextVisit?.active_travel?.status === 'in_progress' ||
    normalizedWorkflowState === 'en_route';

  const isArrived =
    normalizedWorkflowState === 'arrived';

  const isClockedIn =
    nextVisit?.active_clock?.status === 'clocked_in' ||
    normalizedWorkflowState === 'in_progress';

  const canStartTravel =
    normalizedWorkflowState === 'scheduled';

  const canRecordArrival =
    normalizedWorkflowState === 'en_route';

  const canClockOn =
    normalizedWorkflowState === 'arrived' &&
    !isClockedIn;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6" data-testid="staff-dashboard">
      <LiveLocationHeartbeat
        isClockedOn={isClockedIn}
        visitId={nextVisit?.id || nextVisit?._id || null}
        shiftId={nextVisit?.active_clock?.id || nextVisit?.active_clock?._id || null}
      />


      {/* Welcome section */}
      <div className="space-y-1">
        <h1 className="font-heading text-2xl font-bold text-foreground">
          Hello, {user?.full_name?.split(' ')[0] || 'Staff'}
        </h1>
        <p className="text-muted-foreground">
          {formatDate(new Date().toISOString())} • {new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>

      {/* GPS Status indicator */}
      <div className={`gps-indicator ${gpsStatus === 'acquired' ? 'active' : 'inactive'}`}>
        <Navigation className="w-4 h-4" />
        <span>
          {gpsStatus === 'idle' && 'GPS Ready'}
          {gpsStatus === 'acquiring' && 'Getting location...'}
          {gpsStatus === 'acquired' && 'Location acquired'}
          {gpsStatus === 'error' && 'GPS unavailable'}
        </span>
      </div>

      {/* Next Visit Card */}
      {nextVisit ? (
        <Card className="visit-card border-l-4 border-l-secondary" data-testid="next-visit-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">Next Visit</CardTitle>
              <Badge className={getStatusClass(nextVisit.status)}>
                {capitalize(nextVisit.status)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client info */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-secondary mt-0.5" />
                <div>
                  <p className="font-semibold text-lg" data-testid="visit-client-name">{nextVisit.client_name}</p>
                  <p className="text-sm text-muted-foreground">{nextVisit.service_type}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary mt-0.5" />
                <p className="text-sm" data-testid="visit-address">{nextVisit.address}</p>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-secondary mt-0.5" />
                <div>
                  <p className="font-medium" data-testid="visit-date">{formatDate(nextVisit.date)}</p>
                  <p className="text-sm text-muted-foreground" data-testid="visit-time">
                    {formatTime(nextVisit.start_time)} - {formatTime(nextVisit.end_time)}
                  </p>
                </div>
              </div>

              {nextVisit.notes && (
                <div className="bg-muted rounded-lg p-3">
                  <p className="text-sm text-muted-foreground">{nextVisit.notes}</p>
                </div>
              )}
            </div>

            {/* Workflow action buttons */}
            <div className="space-y-3 pt-2">
              {!isClockedIn && (
                <div className="space-y-3">
                  {canStartTravel && (
                    <Button
                      onClick={handleStartTravel}
                      disabled={actionLoading === 'travel'}
                      className="action-btn bg-blue-600 hover:bg-blue-700 text-white w-full"
                      data-testid="start-travel-btn"
                    >
                      {actionLoading === 'travel' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Car className="w-5 h-5 mr-2" />
                          Start Travel
                        </>
                      )}
                    </Button>
                  )}

                  {canRecordArrival && (
                    <Button
                      onClick={handleArrived}
                      disabled={actionLoading === 'arrived'}
                      className="action-btn bg-amber-500 hover:bg-amber-600 text-white w-full"
                      data-testid="arrived-btn"
                    >
                      {actionLoading === 'arrived' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <MapPin className="w-5 h-5 mr-2" />
                          Arrived
                        </>
                      )}
                    </Button>
                  )}

                  {canClockOn && (
                    <Button
                      onClick={handleClockOn}
                      disabled={actionLoading === 'clockOn'}
                      className="action-btn-secondary w-full"
                      data-testid="clock-on-btn"
                    >
                      {actionLoading === 'clockOn' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Play className="w-5 h-5 mr-2" />
                          Clock On
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}

              {isClockedIn && (
                <>
                  <div className="bg-secondary/10 border border-secondary/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-secondary animate-pulse" />
                      <span className="font-semibold text-secondary">
                        Shift in Progress
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Started at {
                        nextVisit?.active_clock?.clock_on_time
                          ? formatTime(
                              nextVisit.active_clock.clock_on_time
                            )
                          : 'current shift'
                      }
                    </p>
                  </div>

                  <Button
                    onClick={handleClockOff}
                    disabled={actionLoading === 'clockOff'}
                    className="action-btn bg-red-600 hover:bg-red-700 text-white w-full"
                    data-testid="clock-off-btn"
                  >
                    {actionLoading === 'clockOff' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Square className="w-5 h-5 mr-2" />
                        Clock Off
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="visit-card" data-testid="no-visits-card">
          <CardContent className="empty-state py-12">
            <CheckCircle className="empty-state-icon w-16 h-16" />
            <p className="empty-state-title">All Done!</p>
            <p className="empty-state-description">
              You have no upcoming visits scheduled. Enjoy your break!
            </p>
          </CardContent>
        </Card>
      )}

      {/* Travel status indicator */}
      {isTraveling && nextVisit?.active_travel && (
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800" data-testid="travel-status-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-blue-900 dark:text-blue-100">Traveling to client</p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Started {formatTime(nextVisit.active_travel.start_time)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visits Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upcoming" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Upcoming ({upcomingVisits.length})
          </TabsTrigger>
          <TabsTrigger value="past" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Past ({pastVisits.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-4 space-y-3">
          {upcomingVisits.length > 0 ? (
            upcomingVisits.map(visit => (
              <Card 
                key={visit.id} 
                className="cursor-pointer hover:border-secondary/50 transition-colors"
                onClick={() => navigate(`/visits/${visit.id}`)}
                data-testid={`upcoming-visit-${visit.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{visit.client_name}</span>
                        <Badge className={getStatusClass(visit.status)} variant="secondary">
                          {capitalize(visit.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(visit.date)} • {formatTime(visit.start_time)} - {formatTime(visit.end_time)}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">{visit.address}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <p className="text-center text-muted-foreground py-8">No upcoming visits</p>
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-4 space-y-3">
          {pastVisits.length > 0 ? (
            pastVisits.map(visit => (
              <Card 
                key={visit.id} 
                className="cursor-pointer hover:border-secondary/50 transition-colors"
                onClick={() => navigate(`/visits/${visit.id}`)}
                data-testid={`past-visit-${visit.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{visit.client_name}</span>
                        <Badge className={getStatusClass(visit.status)} variant="secondary">
                          {capitalize(visit.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(visit.date)} • {formatTime(visit.start_time)} - {formatTime(visit.end_time)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <p className="text-center text-muted-foreground py-8">No past visits</p>
          )}
        </TabsContent>
      </Tabs>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          className="h-14 rounded-xl"
          onClick={() => navigate('/visits')}
          data-testid="view-all-visits-btn"
        >
          <Calendar className="w-5 h-5 mr-2" />
          All Visits
        </Button>
        <Button
          variant="outline"
          className="h-14 rounded-xl"
          onClick={() => navigate('/timesheets')}
          data-testid="view-timesheets-btn"
        >
          <Clock className="w-5 h-5 mr-2" />
          Timesheets
        </Button>
      </div>

      {/* Mileage Summary Card */}
      {mileageSummary && mileageSummary.total_km > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" data-testid="mileage-summary-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Gauge className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-blue-900">Mileage This Period</p>
                  <p className="text-sm text-blue-700">{mileageSummary.total_trips} trips • {mileageSummary.pending_count} pending approval</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-900">{mileageSummary.total_km}</p>
                <p className="text-sm text-blue-600">km total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Mileage Indicator */}
      {activeMileage && (
        <Card className="bg-amber-50 border-amber-200" data-testid="active-mileage-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <Car className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">Mileage Tracking Active</p>
                  <p className="text-sm text-amber-700">Started at {activeMileage.start_odometer.toLocaleString()} km</p>
                </div>
              </div>
              <Badge className="bg-amber-500 text-white">Recording</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Odometer Dialog */}
      <Dialog 
        open={odometerDialog.open} 
        onOpenChange={(open) => !open && setOdometerDialog({ open: false, type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="w-5 h-5" />
              {odometerDialog.type === 'start' ? 'Start Shift' : 'End Shift'}
            </DialogTitle>
            <DialogDescription>
              {odometerDialog.type === 'start' 
                ? 'Enter your current odometer reading to start tracking mileage for this shift.'
                : activeMileage ? `Enter shift notes, then your current odometer reading. Started at ${activeMileage?.start_odometer?.toLocaleString() || 0} km.` : 'Enter shift notes to complete this shift. No KM tracking was started.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {odometerDialog.type === 'start' ? (
              <div className="space-y-4">
                <div>
                  <Label>KM Tracking</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      type="button"
                      variant={trackMileage ? 'default' : 'outline'}
                      onClick={() => setTrackMileage(true)}
                    >
                      Yes, record KM
                    </Button>
                    <Button
                      type="button"
                      variant={!trackMileage ? 'default' : 'outline'}
                      onClick={() => {
                        setTrackMileage(false);
                        setStartOdometer('');
                      }}
                    >
                      No KM / Cleaning Shift
                    </Button>
                  </div>
                </div>

                {trackMileage && (
                  <div className="space-y-2">
                    <Label htmlFor="start-odometer">Start Odometer (km)</Label>
                    <Input
                      id="start-odometer"
                      type="number"
                      placeholder="e.g., 45000"
                      value={startOdometer}
                      onChange={(e) => setStartOdometer(e.target.value)}
                      className="text-lg font-mono"
                      data-testid="start-odometer-input"
                      min="0"
                      step="0.1"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Shift Notes *</Label>
                  <textarea
                    className="w-full rounded-lg border border-slate-600 bg-slate-950 p-3 mt-2 text-slate-100 placeholder:text-slate-400"
                    rows={4}
                    placeholder="Enter care notes, observations, participant wellbeing, incidents, medication reminders or follow-up items..."
                    value={shiftNotes}
                    onInput={(e) => setShiftNotes(e.currentTarget.value)}
                    onChange={(e) => setShiftNotes(e.target.value)}
                    data-testid="shift-notes-input"
                  />
                </div>

                {activeMileage ? (
                  <div className="space-y-2">
                    <Label htmlFor="end-odometer">End Odometer (km)</Label>
                    <Input
                      id="end-odometer"
                      type="number"
                      placeholder="e.g., 45050"
                      value={endOdometer}
                      onChange={(e) => setEndOdometer(e.target.value)}
                      className="text-lg font-mono"
                      data-testid="end-odometer-input"
                      min={activeMileage?.start_odometer || 0}
                      step="0.1"
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                    No KM tracking was started for this shift. Only shift notes are required to clock off.
                  </div>
                )}

                {endOdometer && activeMileage && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-emerald-700">Trip Distance:</span>
                      <span className="text-lg font-bold text-emerald-700" data-testid="calculated-km">
                        {Math.max(0, parseFloat(endOdometer || 0) - activeMileage.start_odometer).toFixed(1)} km
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setOdometerDialog({ open: false, type: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={odometerDialog.type === 'start' ? handleClockOnWithOdometer : handleClockOffWithOdometer}
              disabled={
                !!actionLoading ||
                (odometerDialog.type === 'start' && trackMileage && !startOdometer) ||
                (odometerDialog.type === 'end' && (!shiftNotes.trim() || (activeMileage && !endOdometer)))
              }
              className="bg-secondary hover:bg-secondary/90 disabled:opacity-50"
              data-testid="confirm-odometer-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : odometerDialog.type === 'start' ? (
                <Play className="w-4 h-4 mr-2" />
              ) : (
                <Square className="w-4 h-4 mr-2" />
              )}
              {odometerDialog.type === 'start' ? 'Clock On' : 'Clock Off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
